import GpxParser from "gpxparser";
import { decodeGpxName } from "./decodeGpxText";
import distance from "@turf/distance";
import simplify from "@turf/simplify";
import { elevationGainFromAltitudes, elevationLossFromAltitudes } from "../elevation/elevationTotals";
import { GpxImportError, GpxImportErrorCode, isMalformedXml } from "./gpxImportError";
import { i18n } from "../../i18n";

/**
 * Turning an imported file into a CreateHike payload, for planned hikes saved by the
 * Import files dialog (apps/import_files, through utils/import/importSave.ts).
 *
 * A hike exported from HiiKER carries ONE <trk> OR <rte> PER DAY — the export runs with
 * `mergeRoutesByProperty: 'hikeDayNumber'` (see DownloadModalExportFileTab.vue, which writes
 * <trk>s, and Hikes.vue, which still writes <rte>s), naming them "<hike> - Day 1", "… - Day 2"
 * and so on. So a multi-day route like the Wicklow Way round-trips only if every one of
 * those is read: the dialog used to take `tracks[0] || routes[0]` and silently dropped
 * every day but the first.
 *
 * Kept as pure functions here, out of the component, so the day-splitting and the payload
 * shape are unit-testable without a file input, an elevation service or Apollo.
 */

/** One point of an imported route, in the shape both the GPX and FIT paths produce. */
export type ImportPoint = {
  lat: number;
  lon: number;
  elevation: number;
};

/** One leg of an imported file: a <trk>, a <rte>, or a whole FIT activity. */
export type ImportSegment = {
  name?: string;
  points: ImportPoint[];
};

/** A segment worked up into what the CreateHike payload needs, per day. */
export type ImportDay = {
  title: string;
  coordinates: number[][];
  distances: number[];
  altitudes: number[];
  /** A routed line string to save instead of the plain one built from `coordinates` (a snapped import day). */
  lineString?: Record<string, unknown>;
};

/**
 * Points below this are left alone; above it the route is simplified to keep the planner
 * (and the payload) manageable. 0.0001 degrees is roughly 11 metres.
 */
export const SIMPLIFY_POINT_THRESHOLD = 500;
export const SIMPLIFY_TOLERANCE = 0.0001;

// gpxparser's queryDirectSelector uses undeclared `idx`/`elem` globals, which ReferenceError
// under strict mode (ESM). It's only hit when a parent element has >1 descendants matching a
// tag — e.g. metadata containing both <author><link/></author> and a top-level <link/>
// (common in Strava exports).
if (!(GpxParser as any).__queryDirectSelectorPatched) {
  (GpxParser as any).prototype.queryDirectSelector = function (node: Element, tagName: string) {
    const nodes = node.querySelectorAll(tagName);
    let trueNode: Element | undefined = nodes[0];
    if (nodes.length > 1) {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as Element;
        if (child && child.tagName === tagName) {
          trueNode = child;
        }
      }
    }
    return trueNode;
  };
  (GpxParser as any).__queryDirectSelectorPatched = true;
}

function toImportPoints(points: any): ImportPoint[] {
  if (!Array.isArray(points)) return [];
  return points
    .map((point: any) => ({
      lat: Number(point?.lat),
      lon: Number(point?.lon),
      elevation: Number.isFinite(Number(point?.ele)) ? Number(point.ele) : 0,
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));
}

/**
 * Every leg of a parsed GPX, in file order.
 *
 * Tracks win when a file carries both kinds, which is what the dialog has always done for
 * the first leg. Note gpxparser flattens all of a <trk>'s <trkseg>s into one point list, so
 * day boundaries survive only as separate <trk>s or <rte>s — which is why HiiKER's export
 * writes one <trk> per day rather than one <trkseg> per day.
 *
 * Legs with fewer than two usable points are dropped: they cannot make a LINESTRING, and a
 * stray one-point <rte> would otherwise become an empty day.
 */
export function selectGpxSegments(parsed: { tracks?: any[]; routes?: any[] } | null | undefined): ImportSegment[] {
  const tracks = Array.isArray(parsed?.tracks) ? parsed!.tracks : [];
  const routes = Array.isArray(parsed?.routes) ? parsed!.routes : [];
  const sources = tracks.length > 0 ? tracks : routes;

  return sources
    .map((source: any) => ({
      // decodeGpxName first: gpxparser hands back the raw serialisation, so a
      // CDATA-wrapped or entity-escaped leg name is not yet its own text.
      name: decodeGpxName(source?.name)?.trim() || undefined,
      points: toImportPoints(source?.points),
    }))
    .filter((segment) => segment.points.length >= 2);
}

/** The hike's own name: the file's <metadata><name>, else the caller's fallback. */
export function hikeTitleFrom(parsed: { metadata?: { name?: string } } | null | undefined, fallback: string): string {
  const metadataName = decodeGpxName(parsed?.metadata?.name);
  const chosen = metadataName && metadataName.trim() !== "" ? metadataName : fallback;
  return stripEmoji(chosen);
}

/**
 * Parse a GPX document into its legs plus the hike title, simplifying long ones.
 * Throws the same GpxImportErrors the dialog already reports for unusable files.
 */
export function parseGpxSegments(text: string, fallbackTitle: string): { hikeTitle: string; segments: ImportSegment[] } {
  if (!text || text.trim() === "") {
    throw new GpxImportError(GpxImportErrorCode.Empty);
  }
  if (isMalformedXml(text)) {
    throw new GpxImportError(GpxImportErrorCode.MalformedXml);
  }

  const parsed = new GpxParser();
  parsed.parse(text);

  const segments = selectGpxSegments(parsed).map((segment) => ({
    ...segment,
    points: simplifyPoints(segment.points),
  }));

  if (segments.length === 0) {
    throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
  }

  return { hikeTitle: hikeTitleFrom(parsed, fallbackTitle), segments };
}

/** Thin out a long route; anything at or below the threshold is returned untouched. */
export function simplifyPoints(points: ImportPoint[]): ImportPoint[] {
  if (!Array.isArray(points) || points.length <= SIMPLIFY_POINT_THRESHOLD) return points;

  const lineString = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "LineString",
      coordinates: points.map((p) => [p.lon, p.lat, p.elevation]),
    },
  };

  const simplified = simplify(lineString as any, { tolerance: SIMPLIFY_TOLERANCE, highQuality: true });

  return (simplified as any).geometry.coordinates.map((coord: number[]) => ({
    lon: coord[0],
    lat: coord[1],
    elevation: coord[2] || 0,
  }));
}

/**
 * The title for one day of the import. HiiKER's own export already names each <trk>/<rte>
 * ("Wicklow Way - Day 3"), so that wins; a file whose legs are unnamed gets the day
 * appended instead. A single-leg file keeps the plain hike title, as before.
 */
export function dayTitle(hikeTitle: string, segment: ImportSegment, index: number, total: number): string {
  if (segment.name) return stripEmoji(segment.name);
  if (total <= 1) return hikeTitle;
  return i18n.global.t("utils.gpx_hike_import.day_title", { title: hikeTitle, day: index + 1 });
}

export function processTrackData(points: ImportPoint[], title: string): ImportDay {
  const coordinates: number[][] = [];
  const altitudes: number[] = [];

  points.forEach((p) => {
    coordinates.push([p.lon, p.lat, p.elevation || 0]);
    altitudes.push(p.elevation || 0);
  });

  return {
    title,
    coordinates,
    distances: calculateDistances(coordinates),
    altitudes,
  };
}

export function calculateDistances(coordinates: number[][]): number[] {
  let cumulativeDistance = 0;
  const distances = [0];

  for (let i = 1; i < coordinates.length; i++) {
    const segmentDistance = distance(
      [coordinates[i - 1][0], coordinates[i - 1][1]],
      [coordinates[i][0], coordinates[i][1]],
      { units: "meters" }
    );
    cumulativeDistance += segmentDistance;
    distances.push(Math.round(cumulativeDistance * 10) / 10);
  }

  return distances;
}

/**
 * The import preview's headline climb and descent, on the same moving-baseline rule the server
 * recomputes the track with, so the figures the dialog shows are the figures the hike ends up
 * with. Summing raw deltas instead read about 1.28x high on real GPX, because DEM and GPS
 * jitter bank as climb.
 */
export function calculateElevationGain(altitudes: number[]): number {
  return elevationGainFromAltitudes(altitudes);
}

export function calculateElevationLoss(altitudes: number[]): number {
  return elevationLossFromAltitudes(altitudes);
}

export function createLineStringWKT(coordinates: number[][], distances: number[]): string {
  const points = coordinates
    .map((coord, i) => {
      const lon = coord[0];
      const lat = coord[1];
      const ele = coord[2] || 0;
      const dist = distances[i] || 0;
      return `${lon} ${lat} ${ele} ${dist}`;
    })
    .join(", ");

  return `LINESTRING (${points})`;
}

export function stripEmoji(str: string): string {
  if (!str) return str;
  return str
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function markerAttributesFor(coordinate: number[]) {
  return {
    elevationMeters: Math.round(coordinate[2] || 0),
    lonlat: `POINT(${coordinate[0]} ${coordinate[1]})`,
    snap: false,
  };
}

/**
 * The CreateHike input for an imported file: one planned trail per day, positioned and
 * day-numbered in file order, with the hike's headline stats summed over the days.
 *
 * hikeDaysAttributes is sent for every import, not just multi-day ones, because
 * HikeContentCreationWorker only auto-creates Day 1 when the payload omits it — a
 * seven-day import would otherwise land with seven planned trails and a single hike day,
 * and Hike#itinerary_day_count (which is hike_days.size) would report it as single-day.
 * Shape matches what the planner saves (getPlannedTrailsAttributesForHike /
 * getHikeDaysAttributes), so an imported hike and a planned one are indistinguishable.
 */
export function buildCreateHikeInput(
  { hikeTitle, fileName, days }: { hikeTitle: string; fileName: string; days: ImportDay[] }
) {
  const plannedTrailsAttributes = days.map((day, index) => ({
    title: day.title,
    markersAttributes: [
      markerAttributesFor(day.coordinates[0]),
      markerAttributesFor(day.coordinates[day.coordinates.length - 1]),
    ],
    lineStringsAttributes: [day.lineString ?? {
      geometry: createLineStringWKT(day.coordinates, day.distances),
      surfaces: new Array(day.coordinates.length).fill(0), // Unknown surface
      distances: day.distances,
    }],
    position: index + 1,
    public: false,
    routeStyle: "route",
    day: index + 1,
  }));

  const distanceMeters = days.reduce((total, day) => total + (day.distances[day.distances.length - 1] || 0), 0);
  const elevationGainMeters = days.reduce((total, day) => total + calculateElevationGain(day.altitudes), 0);
  const elevationLossMeters = days.reduce((total, day) => total + calculateElevationLoss(day.altitudes), 0);

  return {
    name: hikeTitle,
    description: i18n.global.t("utils.gpx_hike_import.imported_from", { fileName }),
    publiclyVisible: false,
    difficulty: "Medium",
    distanceMeters: Math.round(distanceMeters),
    elevationGainMeters: Math.round(elevationGainMeters),
    elevationLossMeters: Math.round(elevationLossMeters),
    plannedTrailsAttributes,
    hikeDaysAttributes: days.map((_, index) => ({ dayNumber: index + 1 })),
  };
}
